import React from 'react';
import PropTypes from 'prop-types';
import ContentEditor from '../../ContentEditor';
import NodeActivityEditor from '../../NodeActivityEditor';
import LegacyMedia from '../../LegacyMedia';
import CorrectIcon from '../../../../public/assets/images/1.1/panels/editor/quiz/black-checkmark.svg';
import RemoveIcon from '../../../../public/assets/images/editor/delete-icon-black.svg';
import UploadImageIcon from '../../../icons/upload-image.svg';
import { formatBody } from '../../../utils';
import {
  AlternativeWrapper,
  NewAlternative,
  ActionPanel,
  MarkCorrect,
  InsertImage,
  DeleteAlternative,
} from '../styles';
import { AddButton } from '../../NodeContentEditor/styles';

const QuizNodeActivityEditor = ({
  question,
  index: questionIndex,
  onChange,
  type,
  fileService,
  loading = false,
  addAlternative,
  removeAlternative,
  markAlternative,
  disabled,
  removeLegacyMedia,
}) => {
  const useAutoSave = window.editor.useAutoSave;

  const getId = (editorType, parentIndex, index) =>
    editorType === 'question' ? `question_editor_${index}` : `question_editor_${parentIndex}_${index}`;

  const isDisabled = (id) => useAutoSave && !id;

  const getEditor = (editorType, index, value, parentIndex, id) => (
    <ContentEditor
      id={getId(editorType, parentIndex, index)}
      type={type}
      aria-label={i18n.__('default_texteditor_placeholder')}
      placeholder={i18n.__('default_texteditor_placeholder')}
      value={formatBody(value)}
      onChange={(value) => !loading && onChange(value, index, parentIndex)}
      fileService={fileService}
      canToggleToolbar={false}
      showToolbar={true}
      loading={loading}
      theme="bubble"
      toolbar={[['bold', 'italic', 'underline'], ['image']]}
      className={editorType}
      disabled={disabled || loading || isDisabled(id)}
      focus={!loading && !isDisabled(id)}
    />
  );

  const uploadAlternativeImage = (parentIndex, index) => {
    const fileUpload = document.querySelector(`#file_${getId('alternative', parentIndex, index)}`);
    fileUpload.click();
  };

  const alternatives = (question.alternatives || question.QuizAlternatives || []).map((alternative, index) => {
    const alternativeEditor = getEditor('alternative', index, alternative.body, questionIndex, alternative.id);
    const removeLegacyAlternativeMedia = () => removeLegacyMedia(index, questionIndex);
    return (
      <AlternativeWrapper key={`${questionIndex}_${index}`}>
        {alternative.media_preview ? (
          <div>
            {alternativeEditor}
            <LegacyMedia item={alternative} onRemove={removeLegacyAlternativeMedia} />
          </div>
        ) : (
          alternativeEditor
        )}
        <ActionPanel>
          <MarkCorrect
            src={CorrectIcon}
            onClick={() => markAlternative(questionIndex, index)}
            disabled={loading}
            selected={!!alternative.correct}
            title={i18n.__('quiz_mark_correct_answer')}
          />
          <InsertImage
            src={UploadImageIcon}
            title={i18n.__('toolbar_button_tooltip_image')}
            onClick={() => uploadAlternativeImage(questionIndex, index)}
            disabled={loading}
          />
          <DeleteAlternative
            src={RemoveIcon}
            title={i18n.__('app_delete')}
            onClick={() => removeAlternative(questionIndex, index)}
            disabled={loading}
          />
        </ActionPanel>
      </AlternativeWrapper>
    );
  });

  const alternativePanel = (
    <>
      {alternatives}
      <NewAlternative>
        <AddButton
          onClick={() => addAlternative(questionIndex)}
          disabled={loading}
          title={i18n.__('activity_add_alternative')}
        >
          {i18n.__('activity_add_alternative')}
        </AddButton>
      </NewAlternative>
    </>
  );

  const editorText = type === 'written_quiz' ? i18n.__('written_quiz_student_input_placeholder') : null;
  const editorPanel = type === 'quiz' ? alternativePanel : null;

  return (
    <NodeActivityEditor
      index={questionIndex}
      type={type}
      removeLegacyMedia={removeLegacyMedia}
      legacyContent={question}
      headerPanel={getEditor('question', questionIndex, question.body, null, question.id)}
      className="question"
      editorText={editorText}
      editorPanel={editorPanel}
      disabled={loading}
    />
  );
};

export default QuizNodeActivityEditor;

QuizNodeActivityEditor.propTypes = {
  question: PropTypes.object.isRequired,
  index: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  fileService: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  addAlternative: PropTypes.func,
  removeAlternative: PropTypes.func,
  markAlternative: PropTypes.func,
  removeLegacyMedia: PropTypes.func,
};
